import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { BulkOrderContext, FileDetails } from './BulkOrderContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faDownload, faEye, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faCheckCircle } from '@fortawesome/pro-duotone-svg-icons';
import FileUpload from './FileUpload';
import type { ModalControls } from 'hooks/useModalControls';
import { useFirebaseConfigValues } from 'hooks/useFirebaseConfigValues';
import { Btn } from 'components/btn/Btn';
import { LinkBtn } from 'components/link-btn';
import { Value } from 'firebase/remote-config';
import { styles } from './styles';
import { delay } from 'common/utils';
import { colors } from 'styles/settings';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

const getNumber = (v: Value) => v.asNumber();
const MAX_FILE_SIZE = 100 * 1024;

export const Step4 = ({ recipientsListModal }: { recipientsListModal: ModalControls }) => {
    const { state, setStep, setRecipients, setFileDetails } = useContext(BulkOrderContext);

    const [error, setError] = useState('');

    const step = state.step;

    const max_cart_line_items = useFirebaseConfigValues('max_cart_line_items', 0, getNumber);
    const [uploadAnimationRunning, setUploadAnimationRunning] = useState(false);
    const fileDetails = state.fileDetails as FileDetails;

    const onReviewOrder = () => {
        if (!fileDetails || fileDetails.size <= 0) {
            setError('You must upload a file before proceeding to the next step.');
            return;
        }

        if (error) {
            return;
        }

        setStep(step + 1);
    };

    // Clear error state when user navigate to other step.
    useEffect(() => {
        setError('');
    }, [step]);

    const reviewOrderEnabled = useMemo(() => {
        return !uploadAnimationRunning;
    }, [uploadAnimationRunning]);

    const openRecipientsTable = () => {
        recipientsListModal.toggleModalVisibility();
    };

    const downloadTemplate = useCallback(() => {
        const demoCSV = `Recipient Name,Recipient Email`;

        const file = new Blob([demoCSV], { type: 'text/plain' });
        const downloadLink = document.createElement('a');
        downloadLink.download = 'RaiseRight_eGift_Recipients.csv';
        downloadLink.href = window.URL.createObjectURL(file);
        downloadLink.style.display = 'none';
        document.body.appendChild(downloadLink);
        downloadLink.click();
    }, []);

    const uploadStatusMessage = useMemo(() => {
        if (uploadAnimationRunning) {
            return 'Uploading your file...';
        }

        if (fileDetails && !error) {
            return (
                <span className="flex">
                    Upload Complete
                    <span className="ml-2">
                        <FontAwesomeIcon
                            size="lg"
                            style={{
                                // @ts-ignore
                                '--fa-primary-color': colors.alertGreen,
                                '--fa-secondary-color': colors.alertGreen,
                                '--fa-secondary-opacity': '0.2',
                            }}
                            icon={faCheckCircle}
                        />
                    </span>
                </span>
            );
        }

        return 'Upload';
    }, [uploadAnimationRunning, fileDetails, error]);

    return (
        <>
            <div className={styles.stepContentWrapper}>
                <div className={styles.formHeader}>
                    <h2 className="mb-1">Add Recipient Details</h2>

                    <div className="flex flex-col justify-center">
                        <div className="text-grey-2 text-center text-2xl font-normal">
                            Use our template to upload a file with your
                            <br /> recipients’ information ({max_cart_line_items} maximum).
                        </div>
                    </div>
                </div>
                <div className="mx-auto max-w-md w-full">
                    <div className="flex mb-2.5 center justify-around">
                        <div className="font-semibold text-xl leading-160 text-gray-700 text-grey-darkest mr-2.5">
                            {uploadStatusMessage}
                        </div>
                        {!state.fileDetails ? (
                            <LinkBtn
                                onClick={downloadTemplate}
                                className="ml-auto font-sans text-sm font-semibold leading-22 tracking-normal text-center text-brand no-underline"
                            >
                                <FontAwesomeIcon icon={faDownload} /> Download Template
                            </LinkBtn>
                        ) : (
                            <LinkBtn
                                onClick={() => {
                                    setFileDetails(undefined);
                                    setRecipients([]);
                                }}
                                className="ml-auto font-semibold text-base leading-160 text-center text-error no-underline"
                            >
                                {!uploadAnimationRunning ? (
                                    <>
                                        <FontAwesomeIcon icon={faTrash} /> Remove
                                    </>
                                ) : (
                                    <span className="text-sm font-semibold text-brand">
                                        <FontAwesomeIcon
                                            className="mr-1"
                                            size="lg"
                                            icon={faTimes}
                                            color={colors.brand}
                                        />
                                        Cancel
                                    </span>
                                )}
                            </LinkBtn>
                        )}
                    </div>

                    <FileUpload
                        fileDetails={state.fileDetails as any}
                        setFileDetails={async (value) => {
                            setUploadAnimationRunning(true);
                            setFileDetails(value);
                            await delay(3000);
                            setUploadAnimationRunning(false);
                        }}
                        setRecipients={setRecipients}
                        setError={setError}
                        maxFileSize={MAX_FILE_SIZE}
                    />

                    <div className="mt-2 text-error font-open-sans text-sm leading-normal">
                        {error}
                    </div>

                    {fileDetails && !uploadAnimationRunning && !error ? (
                        <div className="flex w-full mt-12">
                            <LinkBtn
                                onClick={openRecipientsTable}
                                className="mx-auto text-base leading-24 underline text-brand"
                            >
                                <FontAwesomeIcon icon={faEye} /> View Details
                            </LinkBtn>
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="flex flex-col items-center">
                <div className="mb-2">
                    <Btn
                        className={styles.stepperButton}
                        onClick={onReviewOrder}
                        disabled={!reviewOrderEnabled || !!error}
                    >
                        Review Order
                    </Btn>
                </div>
                <div>
                    <button className={styles.stepperButtonClear} onClick={() => setStep(step - 1)}>
                        Back
                    </button>
                </div>
            </div>
        </>
    );
};
